import { Injectable } from '@angular/core';
import { ApiService } from "./api.service";
import { FileUploadDTO } from "../models/customer-agreement.interface";
import { firstValueFrom } from 'rxjs';
import { FileEntity } from '../models/common-model.interface';

export enum FileSource{
  CUSTOMER_AGREEMENT = "CUSTOMER_AGREEMENT",
  IGM = "IGM",
  EIR = "EIR",
  PAYMENT_RECEIPT="PAYMENT_RECEIPT",
  USER_PROFILE_PICTURE="USER_PROFILE_PICTURE",
  OTHER="OTHER",
  VENDOR="VENDOR",
  CONTACT="CONTACT"
}

export function isFileModified(file?: File, existing?: FileUploadDTO): boolean {
  if (!file) return false;
  if (!existing) return true;
  return file.name !== existing.fileName || file.type !== existing.mimeType;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {

  constructor(private apiService: ApiService) { }

  createFormDataToSend(selectedFiles: File[], fileSource: FileSource): FormData[] {
    return selectedFiles.map((file: File) => this.createFormData(file, fileSource));
  }

  createFormData(file: File, fileSource: FileSource): FormData {
    const formDataToSend = new FormData();
    formDataToSend.append('file', file);
    formDataToSend.append('fileSource', fileSource);
    return formDataToSend;
  }

  async uploadFile(file: File, fileSource: FileSource): Promise<string> {
    const formData = this.createFormData(file, fileSource);
    try {
      const res = await firstValueFrom(this.apiService.uploadFile(formData));
      return res.data;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  } 

  async uploadFiles(selectedFiles: File[], fileSource: FileSource): Promise<string[]> {
    const uploadedFileIds: string[] = [];
    const formDataArray = this.createFormDataToSend(selectedFiles, fileSource);

    try {
      if (formDataArray.length > 0) {
        await this.uploadFilesSequentially(formDataArray, uploadedFileIds);
      }
      return uploadedFileIds;
    } catch (error) {
      console.error("File upload failed:", error);
      throw error;
    }
  }

  uploadFilesSequentially(formDataArray: FormData[], uploadedFileIds: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const uploadNext = (index: number) => {
        if (index >= formDataArray.length) {
          resolve();
          return;
        }

        this.apiService.uploadFile(formDataArray[index]).subscribe({
          next: (response: any) => {
            if (response.data) {
              uploadedFileIds.push(response.data);
            }
            uploadNext(index + 1);
          },
          error: (err: any) => {
            reject(err);
          }
        });
      };

      uploadNext(0);
    });
  }

  convertToFileFromFileUploadDto(filesDto?: FileUploadDTO[]): FileEntity[] {
    if (!filesDto) return [];
  
    return filesDto
      .map(upload => this.convertToFileFromDTO(upload))
      .filter((f): f is FileEntity => !!f);
  }

  convertToFileFromDTO(fileDto?: FileUploadDTO): FileEntity | undefined {
    if (!fileDto) return undefined;
    return {
      id: fileDto.id,
      file: new File([""], fileDto.fileName || "unknown", {
              type: fileDto.mimeType || "application/octet-stream",
            })
    }
  }

  buildFileUploadDTO(fileId: string, file: File, fileSource: FileSource): FileUploadDTO {
    return {
      id: fileId,
      fileName: file.name,
      mimeType: file.type,
      fileSource
    };
  }
}
